/**
 * V6 left navigation sidebar — NAVIGATION + BROWSE.
 *
 * Two sections per founder spec 2026-05-24:
 *   1. NAVIGATION — primary surfaces (Markets, Topics, Trending,
 *      News, Feed, Dashboard, Portfolio, Rewards, Settings, Help).
 *   2. BROWSE — category filters (All markets / Sports / Politics /
 *      Crypto / Finance / Geopolitics / Culture / Science / Tech).
 *
 * Bigger row height + pill-style active state. Sticky on desktop
 * (≥md); hidden on mobile/tablet where the global MobileBottomNav
 * handles navigation. Mono Lucide icons everywhere (no colored or
 * illustrated icons per platform icon rule).
 */
"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import {
  BarChart3,
  Hash,
  Flame,
  Newspaper,
  Rss,
  LayoutDashboard,
  Briefcase,
  Gift,
  Settings as SettingsIcon,
  LifeBuoy,
  Crown,
  Trophy,
  Landmark,
  Bitcoin,
  TrendingUp,
  Globe,
  Clapperboard,
  FlaskConical,
  Zap,
  Users,
  type LucideIcon,
} from "lucide-react";

type NavItem = {
  label: string;
  icon: LucideIcon;
  href: string;
  category?: string;
  matchPathPrefix?: string;
};

const NAVIGATION: NavItem[] = [
  { label: "Markets", icon: BarChart3, href: "/markets", matchPathPrefix: "/markets" },
  { label: "Traders", icon: Users, href: "/pi/leaderboard", matchPathPrefix: "/pi" },
  { label: "Topics", icon: Hash, href: "/topics", matchPathPrefix: "/topics" },
  { label: "Trending", icon: Flame, href: "/markets?sort=trending" },
  { label: "News", icon: Newspaper, href: "/feed/news", matchPathPrefix: "/feed/news" },
  { label: "Feed", icon: Rss, href: "/feed", matchPathPrefix: "/feed" },
  { label: "Dashboard", icon: LayoutDashboard, href: "/dashboard", matchPathPrefix: "/dashboard" },
  // 2026-05-24 — Deposit / Withdraw / Transaction history removed from
  // the sidebar nav per founder direction. They now live under the
  // user-pill dropdown in SiteHeader (account-tier actions, not
  // browse-tier nav).
  { label: "Rewards", icon: Gift, href: "/rewards", matchPathPrefix: "/rewards" },
  { label: "Settings", icon: SettingsIcon, href: "/settings", matchPathPrefix: "/settings" },
  { label: "Help", icon: LifeBuoy, href: "/help", matchPathPrefix: "/help" },
];

const BROWSE: NavItem[] = [
  { label: "All markets", icon: Crown, href: "/markets" },
  { label: "Sports", icon: Trophy, href: "/markets?category=sports", category: "sports" },
  { label: "Politics", icon: Landmark, href: "/markets?category=politics", category: "politics" },
  { label: "Crypto", icon: Bitcoin, href: "/markets?category=crypto", category: "crypto" },
  { label: "Finance", icon: TrendingUp, href: "/markets?category=economics", category: "economics" },
  { label: "Geopolitics", icon: Globe, href: "/markets?category=world", category: "world" },
  { label: "Culture", icon: Clapperboard, href: "/markets?category=culture", category: "culture" },
  { label: "Science", icon: FlaskConical, href: "/markets?category=tech", category: "tech" },
  { label: "Tech", icon: Zap, href: "/markets?topic=ai", category: "ai" },
];

export function V6LeftCategorySidebar() {
  const pathname = usePathname();
  const params = useSearchParams();
  const activeCategory = params?.get("category") ?? null;

  function isBrowseActive(item: NavItem): boolean {
    if (item.label === "All markets") {
      return (
        (pathname === "/" || pathname === "/v6" || pathname === "/markets") &&
        !activeCategory
      );
    }
    return item.category != null && activeCategory === item.category;
  }

  function isNavActive(item: NavItem): boolean {
    if (!item.matchPathPrefix) return false;
    return pathname?.startsWith(item.matchPathPrefix) ?? false;
  }

  return (
    <aside
      className="hidden flex-col gap-1 pt-4 md:sticky md:top-4 md:flex md:max-h-[calc(100vh-2rem)] md:overflow-y-auto"
      aria-label="Sidebar"
    >
      {/* Founder direction 2026-05-24 — Browse on top, Navigation
          below. Browse is the high-frequency action (filter the
          markets grid by category); Navigation is the lower-traffic
          jump-to-other-surface list. */}
      <SidebarGroup label="Browse" items={BROWSE} isActive={isBrowseActive} />
      <div className="h-4" />
      <SidebarGroup
        label="Navigation"
        items={NAVIGATION}
        isActive={isNavActive}
      />
    </aside>
  );
}

function SidebarGroup({
  label,
  items,
  isActive,
}: {
  label: string;
  items: NavItem[];
  isActive: (item: NavItem) => boolean;
}) {
  return (
    <>
      <div
        className="px-4 pb-2 pt-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em]"
        style={{ color: "var(--color-text3)" }}
      >
        {label}
      </div>
      {items.map((it) => {
        const Icon = it.icon;
        const active = isActive(it);
        return (
          <Link
            key={it.label}
            href={it.href}
            className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-[14px]"
            style={{
              color: active ? "var(--color-text)" : "var(--color-text2)",
              background: active ? "var(--color-surface2)" : "transparent",
              fontWeight: active ? 600 : 500,
              border: active
                ? "1px solid var(--color-border2)"
                : "1px solid transparent",
            }}
          >
            <Icon size={17} strokeWidth={1.75} aria-hidden="true" />
            <span>{it.label}</span>
          </Link>
        );
      })}
    </>
  );
}
